import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom'
import {  FileTextIcon, SparklesIcon, BrainIcon, HelpCircleIcon, ChevronDown, LogOutIcon, ShieldCheckIcon, PlusCircleIcon, UsersIcon, SettingsIcon, SlidersIcon, LibraryIcon, BarChart3 as ChartBarIcon, Rss, Grid3x3, Zap } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar"
// import logoImage from '@/assets/rainfactory_logo_light.png'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Tables } from '../../database.types'
import { cn } from "@/lib/utils"
import { useBrands } from '@/lib/BrandsProvider'
import { useNavigate } from 'react-router-dom'
import { useRole } from '@/lib/useRole'
import logoImage from '@/assets/logo-large.png'
// import { PaletteIcon } from 'lucide-react'
import { CreditUsageBar } from '@/components/ui/credit-usage-bar'
type Brand = Tables<'brands'>;

// Main menu items
const getMainMenuItems = () => [
  { name: 'Automation', icon: Zap, href: '/automation' },
  // { name: 'Campaigns', icon: LayoutIcon, href: '/campaigns' },
  { name: 'Convergences', icon: SparklesIcon, href: '/convergences' },
  { name: 'Content', icon: FileTextIcon, href: '/content' },
]

// We'll use a function to create this with the actual selected brand ID
const getBrandSetupSubMenuItems = (brandId?: string) => [
  { name: 'Brand Genius', icon: BrainIcon, href: brandId ? `/brands/${brandId}` : '/brand-genius' },
  { name: 'Brand Analysis', icon: ChartBarIcon, href: brandId ? `/brands/${brandId}/analysis` : '/brand-genius' },
  // { name: 'Brand Consultant', icon: MessageCircle, href: brandId ? `/brands/${brandId}/consultant` : '/brands/consultant' },
  { name: 'Brand Settings', icon: SlidersIcon, href: brandId ? `/brands/${brandId}/settings` : '/brand/settings' },
  { name: 'Brand Resources', icon: LibraryIcon, href: brandId ? `/brands/${brandId}/resources` : '/brand/resources' },
  // { name: 'Brand Kit', icon: PaletteIcon, href: `/brand/guide` },
  { name: 'RSS Feed', icon: Rss, href: brandId ? `/brands/${brandId}/rss-feed` : '/brands/rss-feed' },
]

const bottomMenuItems = [
  { name: 'Brands', icon: Grid3x3, href: '/brands' },
  { name: 'Team', icon: UsersIcon, href: '/settings/team' },
  { name: 'Subscription & Billing', icon: SettingsIcon, href: '/settings' },
  { name: 'Guides', icon: HelpCircleIcon, href: '/help' },
]

type SidebarProps = {
  className?: string
}

const SELECTED_BRAND_KEY = 'selectedBrandId';

const LeftSidebar = ({ className }: SidebarProps) => {
  const { brands, selectedBrand, setSelectedBrand } = useBrands()
  const navigate = useNavigate()
  const { isAdmin } = useRole()
  const location = useLocation()
  const [brandSetupOpen, setBrandSetupOpen] = useState(false);
  const [userHasManuallyToggled, setUserHasManuallyToggled] = useState(false);
  const manualToggleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check if a route is active
  const isRouteActive = (path: string): boolean => {
    if (path === '/automation') {
      // Match /automation, /brands/{brandId}/connections, and /brands/{brandId}/connections/{platformId}/settings
      return location.pathname === '/automation' ||
             /^\/brands\/[^\/]+\/connections$/.test(location.pathname) ||
             /^\/brands\/[^\/]+\/connections\/[^\/]+\/settings$/.test(location.pathname)
    }
    if (path === '/campaigns') {
      return location.pathname.startsWith('/campaigns')
    }
    if (path === '/convergences') {
      return location.pathname.startsWith('/convergences')
    }
    if (path === '/content') {
      return location.pathname.startsWith('/content')
    }
    if (path === '/brands') {
      // Match /brands or /brands/* but exclude /brands/{brandId}/connections routes (handled by automation)
      const isConnectionsRoute = /^\/brands\/[^\/]+\/connections/.test(location.pathname)
      return !isConnectionsRoute && (location.pathname === '/brands' || location.pathname.startsWith('/brands/'))
    }
    if (path === '/settings/team') {
      return location.pathname === '/settings/team'
    }
    if (path === '/settings') {
      return location.pathname.startsWith('/settings') && location.pathname !== '/settings/team'
    }
    if (path === '/help') {
      return location.pathname === '/help'
    }
    if (path === '/admin') {
      return location.pathname.startsWith('/admin')
    }
    // Handle brand-specific routes
    if (selectedBrand && path.includes('/brands/')) {
      // If path already includes the brand ID, check directly
      if (path.includes(`/brands/${selectedBrand.id}`)) {
        return location.pathname === path
      }
      // Otherwise, replace generic /brands/ with brand-specific path
      const brandPath = path.replace('/brands/', `/brands/${selectedBrand.id}/`)
      return location.pathname === brandPath
    }
    return location.pathname === path
  }

  // Helper function to check if brand submenu is active
  const isBrandSubmenuActive = (): boolean => {
    if (!selectedBrand) return false
    const brandBasePath = `/brands/${selectedBrand.id}`

    // Exclude connections routes (they belong to Automation menu)
    const isConnectionsRoute = /^\/brands\/[^\/]+\/connections/.test(location.pathname)

    return !isConnectionsRoute && location.pathname.startsWith(brandBasePath) && location.pathname !== brandBasePath
  }

  // Get main menu items
  const mainMenuItems = getMainMenuItems();
  
  // Get submenu items with the current brand ID (only if brand is ACTIVE)
  const brandSetupSubMenuItems = selectedBrand && selectedBrand.status === 'ACTIVE' 
    ? getBrandSetupSubMenuItems(selectedBrand.id)
    : [];

  // Sort brands alphabetically and filter for ACTIVE status only
  const sortedBrands = [...brands]
    .filter(brand => brand.status === 'ACTIVE') // Additional safety filter
    .sort((a, b) => a.brand_name.localeCompare(b.brand_name));

  // Safety check: if selected brand is deleted, clear selection
  useEffect(() => {
    if (selectedBrand && selectedBrand.status === 'DELETED') {
      console.log('LeftSidebar - selected brand is deleted, clearing selection:', selectedBrand);
      setSelectedBrand(null);
      localStorage.removeItem(SELECTED_BRAND_KEY);
    }
  }, [selectedBrand, setSelectedBrand]);

  // Cleanup: if no ACTIVE brands, clear selection
  useEffect(() => {
    if (sortedBrands.length === 0 && selectedBrand) {
      console.log('LeftSidebar - no ACTIVE brands available, clearing selection');
      setSelectedBrand(null);
      localStorage.removeItem(SELECTED_BRAND_KEY);
    }
  }, [sortedBrands, selectedBrand, setSelectedBrand]);

  // Close brand setup menu if no ACTIVE selected brand
  useEffect(() => {
    if (!selectedBrand || selectedBrand.status !== 'ACTIVE') {
      setBrandSetupOpen(false);
      setUserHasManuallyToggled(false); // Reset manual toggle state
    }
  }, [selectedBrand]);

  // Auto-expand Brand menu when user navigates to a brand sub-page
  // Only runs when location changes and respects user manual interaction
  useEffect(() => {
    if (selectedBrand && selectedBrand.status === 'ACTIVE') {
      const shouldExpand = isBrandSubmenuActive();
      
      // Only auto-expand if:
      // 1. We should expand (user is on a brand sub-page)
      // 2. User hasn't manually toggled the menu recently
      if (shouldExpand && !userHasManuallyToggled) {
        setBrandSetupOpen(true);
      }
    }
  }, [location.pathname, selectedBrand]); // Only depend on location and brand changes

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (manualToggleTimeoutRef.current) {
        clearTimeout(manualToggleTimeoutRef.current);
      }
    };
  }, []);

  const handleBrandSelect = async (brand: Brand) => {
    // Safety check: only allow selection of ACTIVE brands
    if (brand.status !== 'ACTIVE') {
      console.warn('Attempted to select non-ACTIVE brand:', brand);
      return;
    }
    
    setSelectedBrand(brand)
    localStorage.setItem(SELECTED_BRAND_KEY, brand.id);
    // Reset manual toggle state when switching brands
    setUserHasManuallyToggled(false);
    
    // Check if we're currently on a brand-specific page and navigate to the new brand's page
    const currentPath = window.location.pathname;
    const brandPathRegex = /^\/brands\/[^\/]+/;
    
    if (brandPathRegex.test(currentPath)) {
      // Extract the current route pattern and replace the brand ID
      const newPath = currentPath.replace(/^\/brands\/[^\/]+/, `/brands/${brand.id}`);
      const searchParams = window.location.search;
      navigate(newPath + searchParams);
    }
  }

  const handleLogout = async () => {
    try {
      navigate('/logout')
    } catch (error) {
      console.error('Error during logout:', error)
    }
  }

  // Combine bottom menu items with conditional Admin item
  const fullBottomMenu = [
    ...(isAdmin ? [{ name: 'Superadmin', icon: ShieldCheckIcon, href: '/admin' }] : []),
    ...bottomMenuItems
  ]

  return (
    <aside className={cn("w-64 shrink-0 flex flex-col h-screen relative overflow-hidden", className)}>
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <SidebarProvider defaultOpen={true}>
        <Sidebar className="flex flex-col h-full relative bg-transparent border-r border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm" variant="sidebar" collapsible="none">
          <SidebarHeader className="border-b border-white/50 flex-shrink-0 relative overflow-hidden shadow-sm">
            {/* Header gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40" />
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-10 translate-x-10" />
            
            <div className="relative w-full flex justify-center px-4 py-3 backdrop-blur-sm">
              <Link to="/" className="text-xl font-bold flex flex-col items-center gap-2 group transition-all duration-300 hover:scale-105">
                <div className="p-2 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                  <img src={logoImage} alt="Rainfactory AI" className="w-12" />
                </div>
                <span className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent group-hover:from-blue-600 group-hover:via-indigo-600 group-hover:to-purple-600 transition-all duration-300">Rainfactory AI</span>
              </Link>
            </div>
            <SidebarMenu>
              <SidebarMenuItem>
                {sortedBrands.length > 0 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton className="h-auto min-h-[2.5rem] py-2">
                        <span className="text-lg font-medium line-clamp-2 max-w-[180px] leading-tight">
                          {selectedBrand && selectedBrand.status === 'ACTIVE' ? selectedBrand.brand_name : "Select a brand"}
                        </span>
                        <ChevronDown className="ml-auto h-4 w-4 flex-shrink-0" />
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[--radix-popper-anchor-width] z-[110]">
                      {sortedBrands
                        .filter(brand => brand.status === 'ACTIVE') // Additional safety filter for dropdown
                        .map((brand) => (
                        <DropdownMenuItem
                          key={brand.id}
                          onClick={() => handleBrandSelect(brand)}
                          className="flex items-center justify-between"
                        >
                          <span className="truncate mr-2">{brand.brand_name}</span>
                          {(brand as any).access_source === 'direct' && (
                            <UsersIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    to="/onboarding"
                    className="flex items-center justify-center gap-2 px-4 py-2 w-full text-primary hover:bg-primary/10 rounded-md transition-colors"
                  >
                    <PlusCircleIcon className="h-5 w-5" />
                    <span>Set up your brand</span>
                  </Link>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>

          <SidebarContent className="flex-1">
            <SidebarMenu>
              {mainMenuItems.map((item) => {
                const isActive = isRouteActive(item.href);
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-3 transition-all duration-300 group relative overflow-hidden",
                          isActive
                            ? "bg-gradient-to-r from-blue-500/90 to-indigo-600/90 text-white shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-0.5"
                            : "hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-indigo-50/60 hover:scale-105 hover:shadow-md hover:-translate-y-0.5"
                        )}
                      >
                        <div className={cn(
                          "absolute inset-0 bg-gradient-to-r transition-opacity duration-300",
                          isActive
                            ? "from-white/10 via-transparent to-white/5 opacity-100"
                            : "from-blue-50/30 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100"
                        )} />
                        <item.icon className={cn(
                          "h-5 w-5 relative z-10 transition-all duration-300",
                          isActive
                            ? "text-white drop-shadow-sm"
                            : "group-hover:text-blue-600 group-hover:scale-110"
                        )} />
                        <span className={cn(
                          "relative z-10 font-medium transition-all duration-300",
                          isActive
                            ? "text-white drop-shadow-sm bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent"
                            : "group-hover:text-blue-700 group-hover:font-semibold"
                        )}>
                          {item.name}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              
              <SidebarMenuItem>
                <SidebarMenuButton 
                  className={cn(
                    "flex items-center gap-3 w-full justify-between transition-all duration-300 group relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    selectedBrand && selectedBrand.status === 'ACTIVE' 
                      ? isBrandSubmenuActive()
                        ? "bg-gradient-to-r from-emerald-500/90 to-teal-600/90 text-white shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-0.5"
                        : "hover:bg-gradient-to-r hover:from-emerald-50/80 hover:to-teal-50/60 hover:scale-105 hover:shadow-md hover:-translate-y-0.5 active:scale-95"
                      : "opacity-50 cursor-not-allowed hover:opacity-50 focus:opacity-50 active:scale-100"
                  )}
                  onClick={() => {
                    if (selectedBrand && selectedBrand.status === 'ACTIVE') {
                      setBrandSetupOpen(!brandSetupOpen);
                      setUserHasManuallyToggled(true); // Mark as manually toggled
                      
                      // Clear any existing timeout
                      if (manualToggleTimeoutRef.current) {
                        clearTimeout(manualToggleTimeoutRef.current);
                      }
                      
                      // Reset manual toggle state after 30 seconds
                      manualToggleTimeoutRef.current = setTimeout(() => {
                        setUserHasManuallyToggled(false);
                      }, 30000);
                    }
                  }}
                  disabled={!selectedBrand || selectedBrand.status !== 'ACTIVE'}
                  aria-label={selectedBrand && selectedBrand.status === 'ACTIVE' ? 'Brand menu' : 'No active brand available'}
                  tabIndex={selectedBrand && selectedBrand.status === 'ACTIVE' ? 0 : -1}
                  role="button"
                  aria-expanded={brandSetupOpen}
                  aria-haspopup="true"
                  aria-controls="brand-setup-submenu"
                  data-testid="brand-setup-menu-button"
                  data-brand-status={selectedBrand?.status || 'none'}
                  data-brand-id={selectedBrand?.id || 'none'}
                  data-brand-name={selectedBrand?.brand_name || 'none'}
                  title={selectedBrand && selectedBrand.status === 'ACTIVE' ? 'Click to open brand menu' : 'No active brand available'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (selectedBrand && selectedBrand.status === 'ACTIVE') {
                        setBrandSetupOpen(!brandSetupOpen);
                        setUserHasManuallyToggled(true); // Mark as manually toggled
                        
                        // Clear any existing timeout
                        if (manualToggleTimeoutRef.current) {
                          clearTimeout(manualToggleTimeoutRef.current);
                        }
                        
                        // Reset manual toggle state after 30 seconds
                        manualToggleTimeoutRef.current = setTimeout(() => {
                          setUserHasManuallyToggled(false);
                        }, 30000);
                      }
                    }
                  }}
                >
                  <div className={cn(
                    "absolute inset-0 bg-gradient-to-r transition-opacity duration-300",
                    selectedBrand && selectedBrand.status === 'ACTIVE' && isBrandSubmenuActive()
                      ? "from-white/10 via-transparent to-white/5 opacity-100"
                      : "from-emerald-50/30 via-transparent to-teal-50/20 opacity-0 group-hover:opacity-100"
                  )} />
                  <div className="flex items-center gap-3 relative z-10">
                    {selectedBrand && selectedBrand.status === 'ACTIVE' ? (
                      <ShieldCheckIcon className={cn(
                        "h-5 w-5 transition-all duration-300",
                        isBrandSubmenuActive()
                          ? "text-white drop-shadow-sm"
                          : "group-hover:text-emerald-600 group-hover:scale-110"
                      )} />
                    ) : (
                      <HelpCircleIcon className="h-5 w-5 text-muted-foreground" />
                    )}
                    <span className={cn(
                      "font-medium transition-all duration-300",
                      selectedBrand && selectedBrand.status === 'ACTIVE'
                        ? isBrandSubmenuActive()
                          ? "text-white drop-shadow-sm bg-gradient-to-r from-white to-emerald-100 bg-clip-text text-transparent"
                          : "group-hover:text-emerald-700 group-hover:font-semibold"
                        : 'text-muted-foreground'
                    )}>
                      {selectedBrand && selectedBrand.status === 'ACTIVE' ? 'Brand' : 'No Active Brand'}
                    </span>
                  </div>
                  {selectedBrand && selectedBrand.status === 'ACTIVE' && (
                    <ChevronDown className={cn(
                      "h-4 w-4 relative z-10 transition-all duration-300",
                      brandSetupOpen ? "transform rotate-0" : "transform -rotate-90",
                      isBrandSubmenuActive()
                        ? "text-white drop-shadow-sm"
                        : "group-hover:text-emerald-600"
                    )} />
                  )}
                </SidebarMenuButton>
                
                {brandSetupOpen && selectedBrand && selectedBrand.status === 'ACTIVE' && (
                  <SidebarMenuSub id="brand-setup-submenu">
                    {brandSetupSubMenuItems.map((item) => {
                      const isActive = isRouteActive(item.href);
                      return (
                        <SidebarMenuSubItem key={item.name}>
                          <SidebarMenuSubButton asChild>
                            <Link 
                              to={item.href} 
                              className={cn(
                                "flex items-center gap-3 transition-all duration-300 group relative overflow-hidden rounded-lg ml-4",
                                isActive
                                  ? "bg-gradient-to-r from-emerald-500/80 to-teal-600/80 text-white shadow-md hover:shadow-lg transform hover:scale-105"
                                  : "hover:bg-gradient-to-r hover:from-emerald-50/60 hover:to-teal-50/40 hover:scale-105 hover:shadow-sm"
                              )}
                            >
                              <div className={cn(
                                "absolute inset-0 bg-gradient-to-r transition-opacity duration-300",
                                isActive
                                  ? "from-white/10 via-transparent to-white/5 opacity-100"
                                  : "from-emerald-50/30 via-transparent to-teal-50/20 opacity-0 group-hover:opacity-100"
                              )} />
                              <item.icon className={cn(
                                "h-5 w-5 relative z-10 transition-all duration-300",
                                isActive
                                  ? "text-white drop-shadow-sm"
                                  : "group-hover:text-emerald-600 group-hover:scale-110"
                              )} />
                              <span className={cn(
                                "relative z-10 text-sm font-medium transition-all duration-300",
                                isActive
                                  ? "text-white drop-shadow-sm"
                                  : "group-hover:text-emerald-700 group-hover:font-semibold"
                              )}>
                                {item.name}
                              </span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarContent>

          {/* Credit Usage Bar */}
          {selectedBrand && selectedBrand.status === 'ACTIVE' && (
            <div className="px-4 py-3 border-b">
              <CreditUsageBar
                showDetails={true}
              />
            </div>
          )}

          <SidebarFooter className="flex-shrink-0 border-t border-white/50 mt-auto shadow-sm">
            <SidebarMenu>
              {fullBottomMenu.map((item) => {
                const isActive = isRouteActive(item.href);
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-3 transition-all duration-300 group relative overflow-hidden",
                          isActive
                            ? "bg-gradient-to-r from-purple-500/90 to-indigo-600/90 text-white shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-0.5"
                            : "hover:bg-gradient-to-r hover:from-purple-50/80 hover:to-indigo-50/60 hover:scale-105 hover:shadow-md hover:-translate-y-0.5"
                        )}
                      >
                        <div className={cn(
                          "absolute inset-0 bg-gradient-to-r transition-opacity duration-300",
                          isActive
                            ? "from-white/10 via-transparent to-white/5 opacity-100"
                            : "from-purple-50/30 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100"
                        )} />
                        <item.icon className={cn(
                          "h-5 w-5 relative z-10 transition-all duration-300",
                          isActive
                            ? "text-white drop-shadow-sm"
                            : "group-hover:text-purple-600 group-hover:scale-110"
                        )} />
                        <span className={cn(
                          "relative z-10 font-medium transition-all duration-300",
                          isActive
                            ? "text-white drop-shadow-sm bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent"
                            : "group-hover:text-purple-700 group-hover:font-semibold"
                        )}>
                          {item.name}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={handleLogout}
                  className="flex items-center gap-3 transition-all duration-300 group relative overflow-hidden hover:bg-gradient-to-r hover:from-red-50/80 hover:to-pink-50/60 hover:scale-105 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-50/30 via-transparent to-pink-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <LogOutIcon className="h-5 w-5 relative z-10 transition-all duration-300 group-hover:text-red-600 group-hover:scale-110" />
                  <span className="relative z-10 font-medium transition-all duration-300 group-hover:text-red-700 group-hover:font-semibold">Log Out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
      </SidebarProvider>
    </aside>
  )
}

export default LeftSidebar
